import store from '@/store'
import { getBaseUrl } from '@/api/CallCentreApiAxiosPlugin'
import eventBus from '@/common/bus'

/**
 * SSRF Protection: Blocked domain patterns
 * These patterns block known SSRF testing/exploitation domains and internal network addresses
 */
const BLOCKED_PATTERNS: RegExp[] = [
  // SSRF testing/exploitation domains
  /oastify\.com/i,
  /burpcollaborator\.net/i,
  /requestbin\.(com|net|io)/i,
  /webhook\.site/i,
  /pipedream\.(com|net)/i,
  /interact\.sh/i,
  /canarytokens\.com/i,
  /beeceptor\.com/i,
  /hookbin\.com/i,
  /requestcatcher\.com/i,
  /mockbin\.org/i,
  /postb\.in/i,
  /httpbin\.org/i,

  // Internal/Private network addresses (RFC1918, localhost, link-local, etc.)
  /^https?:\/\/localhost(:\d+)?/i,
  /^https?:\/\/127\.\d{1,3}\.\d{1,3}\.\d{1,3}/,
  /^https?:\/\/10\.\d{1,3}\.\d{1,3}\.\d{1,3}/,
  /^https?:\/\/172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}/,
  /^https?:\/\/192\.168\.\d{1,3}\.\d{1,3}/,
  /^https?:\/\/169\.254\.\d{1,3}\.\d{1,3}/, // AWS metadata link-local
  /^https?:\/\/\[::1\]/i, // IPv6 localhost
  /^https?:\/\/\[fc00:/i, // IPv6 private
  /^https?:\/\/\[fd00:/i, // IPv6 private
  /^https?:\/\/\[fe80:/i, // IPv6 link-local

  // Cloud metadata endpoints
  /metadata\.google\.internal/i,
  /metadata\.azure\.com/i,
  /169\.254\.169\.254/, // AWS/GCP/Azure metadata service

  // Dangerous protocols (in case of protocol smuggling attempts)
  /^file:/i,
  /^javascript:/i,
  /^data:/i,
  /^blob:/i,
  /^vbscript:/i,
  /^ftp:/i,
  /^gopher:/i,
  /^dict:/i,
  /^ldap:/i,
  /^tftp:/i,
]

/**
 * Validates a URL for SSRF protection
 * Uses a blocklist approach to deny known malicious domains and internal addresses
 */
function validateUrlSecurity(url: string): { valid: boolean; reason?: string } {
  if (!url || typeof url !== 'string') {
    return { valid: false, reason: 'Invalid URL provided.' }
  }

  // Trim and normalize the URL
  const normalizedUrl = url.trim()

  // Empty URLs are invalid
  if (normalizedUrl === '') {
    return { valid: false, reason: 'URL cannot be empty.' }
  }

  // Allow relative URLs (they don't trigger SSRF as they stay on the same domain)
  if (normalizedUrl.startsWith('/') && !normalizedUrl.startsWith('//')) {
    return { valid: true }
  }

  // Allow anchor links
  if (normalizedUrl.startsWith('#')) {
    return { valid: true }
  }

  // Allow mailto and tel links (they don't fetch resources)
  if (normalizedUrl.startsWith('mailto:') || normalizedUrl.startsWith('tel:')) {
    return { valid: true }
  }

  // Allow data:image/ URLs (base64 encoded images)
  if (normalizedUrl.toLowerCase().startsWith('data:image/')) {
    return { valid: true }
  }

  try {
    // Handle protocol-relative URLs
    const urlToCheck = normalizedUrl.startsWith('//') ? `https:${normalizedUrl}` : normalizedUrl

    const urlObj = new URL(urlToCheck)

    // Only allow HTTP/HTTPS protocols for absolute URLs
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return {
        valid: false,
        reason: `Protocol "${urlObj.protocol.replace(
          ':',
          ''
        )}" is not allowed. Only HTTP and HTTPS links are permitted.`,
      }
    }

    // Check against blocked patterns
    for (const pattern of BLOCKED_PATTERNS) {
      if (pattern.test(normalizedUrl) || pattern.test(urlObj.hostname)) {
        return {
          valid: false,
          reason: 'This URL has been blocked for security reasons.',
        }
      }
    }

    // Block direct IP addresses (prevents bypassing domain blocklist)
    const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/
    const ipv6Pattern = /^\[.*\]$/
    if (ipv4Pattern.test(urlObj.hostname) || ipv6Pattern.test(urlObj.hostname)) {
      return {
        valid: false,
        reason: 'Direct IP addresses are not allowed. Please use a domain name.',
      }
    }

    // Block URLs with embedded credentials (can be used for SSRF attacks)
    if (urlObj.username || urlObj.password) {
      return {
        valid: false,
        reason: 'URLs with embedded credentials are not allowed.',
      }
    }

    // Block suspicious port numbers (common for internal services)
    const suspiciousPorts = [22, 23, 25, 53, 110, 143, 389, 445, 587, 636, 993, 995, 3306, 3389, 5432, 6379, 27017]
    if (urlObj.port && suspiciousPorts.includes(parseInt(urlObj.port, 10))) {
      return {
        valid: false,
        reason: `Port ${urlObj.port} is not allowed for security reasons.`,
      }
    }

    return { valid: true }
  } catch {
    return {
      valid: false,
      reason: 'Invalid URL format. Please enter a valid URL.',
    }
  }
}

// Track blocked URLs during paste for notification
let blockedUrlsInPaste: string[] = []

// Dangerous protocols that should be blocked in href/src attributes
// Note: 'data:' is handled separately to allow 'data:image/' for base64 images
const DANGEROUS_PROTOCOLS = ['javascript:', 'vbscript:', 'file:', 'blob:']

// Sanitize pasted content - removes blocked URLs from HTML
function sanitizePastedContent(html: string): string {
  if (!html || typeof html !== 'string') return html

  // Reset blocked URLs tracker
  blockedUrlsInPaste = []

  let sanitized = html

  // Match URLs in href and src attributes using replace with callback
  const attributeUrlRegex = /(href|src)\s*=\s*(["'])([^"']*)\2/gi
  sanitized = sanitized.replace(attributeUrlRegex, (fullMatch, attribute, quote, url) => {
    // First check for dangerous protocols (case-insensitive)
    const lowerUrl = url.toLowerCase().trim()

    // Allow base64 images
    if (lowerUrl.startsWith('data:image/')) {
      return fullMatch
    }

    for (const protocol of DANGEROUS_PROTOCOLS) {
      if (lowerUrl.startsWith(protocol)) {
        blockedUrlsInPaste.push(url)
        return `${attribute}=${quote}#${quote}`
      }
    }

    // Then run the full URL security validation
    const validation = validateUrlSecurity(url)
    if (!validation.valid) {
      blockedUrlsInPaste.push(url)
      return `${attribute}=${quote}#${quote}`
    }
    return fullMatch
  })

  // Check for plain HTTP/HTTPS URLs in text (not in attributes)
  const plainHttpUrlRegex = /(^|[^"'=])(https?:\/\/[^\s<>"']+)/gi
  sanitized = sanitized.replace(plainHttpUrlRegex, (fullMatch, prefix, url) => {
    const validation = validateUrlSecurity(url)
    if (!validation.valid) {
      blockedUrlsInPaste.push(url)
      return `${prefix}[URL REMOVED]`
    }
    return fullMatch
  })

  // Check for dangerous protocol URLs in plain text (javascript:, file:, etc.)
  // Note: data:image/ is allowed for base64 images
  const dangerousProtocolRegex = /(javascript|vbscript|file|blob):[^\s<>]*/gi
  sanitized = sanitized.replace(dangerousProtocolRegex, (match) => {
    blockedUrlsInPaste.push(match)
    return '#'
  })

  // Block data: URLs except for data:image/
  const dataProtocolRegex = /data:(?!image\/)[^\s<>]*/gi
  sanitized = sanitized.replace(dataProtocolRegex, (match) => {
    blockedUrlsInPaste.push(match)
    return '#'
  })

  // Also detect HTML-encoded versions of dangerous protocols (e.g., when HTML is pasted as text)
  const encodedProtocolRegex = /(&lt;a\s+href=["']?)(javascript|vbscript|file|blob):([^&]*?)(&gt;)/gi
  sanitized = sanitized.replace(encodedProtocolRegex, (match, prefix) => {
    blockedUrlsInPaste.push(match)
    return `${prefix}#&gt;`
  })

  return sanitized
}

// Show notification for blocked URLs in pasted content
function notifyBlockedPasteUrls(): void {
  if (blockedUrlsInPaste.length > 0) {
    eventBus.$emit('showSnackbar', 'Some content was removed for security reasons.')
    blockedUrlsInPaste = []
  }
}

// Froala editor instance interface for type safety
interface FroalaEditorInstance {
  $el: {
    find: (selector: string) => {
      each: (callback: (this: HTMLElement) => void) => void
    }
  }
  html: {
    get: () => string
    set: (html: string) => void
  }
  events: {
    trigger: (name: string) => void
  }
  undo: {
    saveStep: () => void
  }
}

// Validate all links in a Froala editor instance and sanitize dangerous ones
function validateAllLinksInEditor(editor: FroalaEditorInstance): void {
  if (!editor || !editor.html) return

  let blockedCount = 0

  try {
    // Get current HTML content and sanitize it
    const currentHtml = editor.html.get()
    if (!currentHtml) return

    // Use the same sanitization function used for paste
    const sanitizedHtml = sanitizePastedContent(currentHtml)

    // If content was changed, update the editor
    if (sanitizedHtml !== currentHtml) {
      blockedCount = blockedUrlsInPaste.length

      // Save undo step before modification
      if (editor.undo && typeof editor.undo.saveStep === 'function') {
        editor.undo.saveStep()
      }

      // Set the sanitized content
      editor.html.set(sanitizedHtml)

      // Notify user
      if (blockedCount > 0) {
        eventBus.$emit(
          'showSnackbar',
          `${blockedCount} potentially dangerous URL(s) were removed for security reasons.`
        )
      }

      // Clear the tracker
      blockedUrlsInPaste = []
    }
  } catch (error) {
    console.error('[SSRF] Error validating links in editor:', error)
  }
}

// Validate image uploads
function validateImageUpload(images: File[]): boolean {
  const maxSize = 5 * 1024 * 1024 // 5MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

  for (const image of images) {
    if (image.size > maxSize) {
      showSecurityError(`Image ${image.name} is too large. Maximum size is 5MB.`)
      return false
    }

    if (!allowedTypes.includes(image.type)) {
      showSecurityError(`Image type ${image.type} is not allowed.`)
      return false
    }
  }

  return true
}

// Show security errors to user using app's snackbar notification system
// Also adds visual feedback to the link popup if visible
function showSecurityError(message: string) {
  // Use the app's global snackbar via eventBus
  eventBus.$emit('showSnackbar', message)

  // Add visual feedback to the link popup input if it exists
  // This highlights the URL input field with an error state
  setTimeout(() => {
    const linkPopup = document.querySelector('.fr-popup.fr-active')
    if (linkPopup) {
      const urlInput = linkPopup.querySelector('input.fr-link-attr[name="href"]') as HTMLInputElement
      if (urlInput) {
        urlInput.style.borderColor = '#f44336'
        urlInput.style.backgroundColor = '#ffebee'
        urlInput.focus()
        urlInput.select()

        // Create and show inline error message if not already present
        let errorDiv = linkPopup.querySelector('.fr-ssrf-error') as HTMLElement
        if (!errorDiv) {
          errorDiv = document.createElement('div')
          errorDiv.className = 'fr-ssrf-error'
          errorDiv.style.cssText = 'color: #f44336; font-size: 12px; padding: 4px 8px; margin-top: 4px;'
          urlInput.parentElement?.appendChild(errorDiv)
        }
        errorDiv.textContent = message

        // Reset styles after 5 seconds
        setTimeout(() => {
          urlInput.style.borderColor = ''
          urlInput.style.backgroundColor = ''
          if (errorDiv) {
            errorDiv.remove()
          }
        }, 5000)
      }
    }
  }, 0)
}

export function froalaConfig(toolbarButtonsRequired = false, url = '', params: Record<string, unknown> | null = null) {
  return {
    key: store.Instance.state.Environment.FroalaEditorKey,

    htmlExecuteScripts: false,
    htmlRemoveTags: [
      'script',
      'plaintext',
      'xmp',
      'iframe',
      'object',
      'embed',
      'applet',
      'meta',
      'link',
      'style',
      'base',
      'svg',
      'math',
      'form',
      'noscript',
      'input',
      'button',
      'textarea',
      'select',
      'video',
      'audio',
      'source',
      'track',
      'template',
    ],

    // Restrict allowed HTML tags (whitelist approach)
    htmlAllowedTags: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'strike',
      'del',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'pre',
      'code',
      'span',
      'div',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'img',
    ],

    // Restrict allowed attributes
    htmlAllowedAttrs: ['href', 'src', 'alt', 'title', 'class', 'id', 'style', 'width', 'height', 'target', 'rel'],

    // Remove comments
    htmlAllowComments: false,

    pastePlain: false, // Keep formatting but sanitize
    pasteDeniedTags: ['script', 'iframe', 'object', 'embed', 'style'],
    pasteDeniedAttrs: ['onerror', 'onload', 'onclick', 'oninput', 'onmouseover'],

    placeholderText: 'Edit Your Content Here!',
    heightMin: 400,
    heightMax: 400,
    quickInsertTags: [''],

    toolbarButtons: toolbarButtonsRequired
      ? [
          'fullscreen',
          'selectAll',
          'lineHeight',
          'undo',
          'redo',
          'bold',
          'italic',
          'underline',
          'fontFamily',
          'fontSize',
          'textColor',
          'backgroundColor',
          'formatOL',
          'formatUL',
          'insertLink',
          'inlineClass',
          'paragraphStyle',
          'paragraphFormat',
          'insertImage',
        ]
      : undefined,

    // Security Configuration
    // CRITICAL: Open all links in new tab for security (prevents navigation away from app)
    linkAlwaysBlank: true, // Forces target="_blank" on all links
    linkAlwaysNoFollow: true, // Adds rel="nofollow"
    linkAutoPrefix: 'https://', // Default to secure protocol
    linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
    linkInsertButtons: ['linkBack'],
    linkList: [],
    linkNoOpener: true, // Adds rel="noopener" - prevents window.opener access
    linkNoReferrer: true, // Adds rel="noreferrer" - prevents referrer leakage

    // CRITICAL: Disable automatic link/URL fetching
    linkPreview: false, // Disable link preview fetching
    linkConvertEmailAddress: false, // Don't auto-convert emails (could trigger validation)

    // Image Security Configuration
    // Always allow imageUpload button for local file selection
    // imageManager only shown if server URL is configured
    imageInsertButtons: url ? ['imageUpload', 'imageManager'] : ['imageUpload'],

    // CRITICAL: Prevent remote URL image loading
    imageUploadRemoteUrls: false, // Don't fetch images from URLs
    imagePaste: true, // Allow pasting images (they get converted to base64)
    imageDefaultDisplay: 'inline', // Don't trigger any display-related fetches
    imageCORSProxy: '', // No CORS proxy (prevents proxy requests)
    imageAddNewLine: false, // Simple behavior, no extra processing

    // Image upload restrictions
    imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif', 'webp'],
    imageMaxSize: 5 * 1024 * 1024, // 5MB

    // Validate image sources
    imageEditButtons: ['imageReplace', 'imageAlign', 'imageRemove', 'imageSize', 'imageAlt'],

    // Image upload configuration
    // If no URL provided, images will be embedded as base64
    imageUploadURL: url ? `${getBaseUrl()}/api/upload-image` : null,
    imageUploadMethod: 'POST',
    imageUploadParams: params ? params : {},
    // When no upload URL, Froala will convert images to base64 automatically
    imageUploadToS3: false,

    // Authorization header
    requestHeaders: url
      ? {
          Authorization: 'bearer ' + store.Instance.state.SessionDetail.accessToken,
        }
      : {},

    // Image manager for existing images only
    imageManagerLoadURL: url ? getBaseUrl() + url : undefined,
    imageManagerLoadMethod: url ? 'GET' : undefined,
    imageManagerLoadParams: params ? params : {},

    // Additional image security
    imageDefaultWidth: 0, // Let images keep their size
    imageResizeWithPercent: false,
    imageRoundPercent: false,
    imageOutputSize: true,

    // Video security (disable completely)
    videoInsertButtons: [], // Disable video insertion
    videoUpload: false,
    videoAllowedTypes: [], // No video types allowed
    videoDefaultDisplay: 'inline',
    videoResize: false,

    // File upload configuration
    // Allow safe document types only - no executables or scripts
    fileAllowedTypes: [
      'application/pdf', // PDF documents
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'text/plain', // .txt
      'text/csv', // .csv
      'application/rtf', // .rtf
    ],
    fileMaxSize: 10 * 1024 * 1024, // 10MB max file size
    fileUploadURL: null, // File upload disabled - requires server configuration
    fileUploadMethod: 'POST',
    fileUploadParams: params ? params : {},
    // File upload disabled - hide button from toolbar
    fileInsertButtons: [],
    fileUpload: false, // Disable file upload completely

    // Disable any external requests for embeds
    embedlyKey: '', // No Embedly integration
    embedlyScriptPath: '', // No Embedly script loading

    // Event handlers for SSRF protection and content validation
    events: {
      // Froala 3.x paste events - we need multiple hooks to catch all paste scenarios
      // 'paste.before' - Called before paste starts, receives clipboard data
      'paste.before': function (this: FroalaEditorInstance) {
        return true // Allow paste to continue
      },
      // 'paste.beforeCleanup' - Called with raw HTML before Froala cleans it
      'paste.beforeCleanup': function (this: FroalaEditorInstance, html: string) {
        const sanitized = sanitizePastedContent(html)
        return sanitized
      },
      // 'paste.afterCleanup' - Called after Froala's cleanup, can still modify
      'paste.afterCleanup': function (this: FroalaEditorInstance, html: string) {
        // Double-check sanitization after Froala's processing
        const sanitized = sanitizePastedContent(html)
        return sanitized
      },
      // 'paste.after' - Called after paste is complete
      'paste.after': function (this: FroalaEditorInstance) {
        // Notify user if any URLs were blocked during paste
        notifyBlockedPasteUrls()
        // CRITICAL: Final fallback - scan and sanitize all links in the editor
        // This catches any URLs that might have slipped through the paste events
        validateAllLinksInEditor(this)
      },
      // 'paste.wordPaste' - Called when pasting from Microsoft Word
      'paste.wordPaste': function (this: FroalaEditorInstance, html: string) {
        return sanitizePastedContent(html)
      },
      // 'html.set' - Called when HTML content is set programmatically
      'html.set': function (this: FroalaEditorInstance) {
        // Validate after HTML is set
        setTimeout(() => validateAllLinksInEditor(this), 0)
      },
      // CRITICAL: Validate links before insertion - primary SSRF protection
      // Note: Froala uses 'link.beforeInsert' for the link plugin event
      'link.beforeInsert': function (this: FroalaEditorInstance, link: string) {
        // Check dangerous protocols first
        const lowerLink = link.toLowerCase().trim()
        for (const protocol of DANGEROUS_PROTOCOLS) {
          if (lowerLink.startsWith(protocol)) {
            showSecurityError(`Protocol "${protocol.replace(':', '')}" is not allowed for security reasons.`)
            return false
          }
        }

        const validation = validateUrlSecurity(link)
        if (!validation.valid) {
          showSecurityError(validation.reason || 'This link is not allowed for security reasons.')
          return false // Prevent insertion
        }
        return true
      },
      'anchor.beforeInsert': function (this: FroalaEditorInstance, link: string) {
        // Check dangerous protocols first
        const lowerLink = link.toLowerCase().trim()
        for (const protocol of DANGEROUS_PROTOCOLS) {
          if (lowerLink.startsWith(protocol)) {
            showSecurityError(`Protocol "${protocol.replace(':', '')}" is not allowed for security reasons.`)
            return false
          }
        }

        const validation = validateUrlSecurity(link)
        if (!validation.valid) {
          showSecurityError(validation.reason || 'This link is not allowed for security reasons.')
          return false // Prevent insertion
        }
        return true
      },
      // Validate images before upload
      'image.beforeUpload': function (this: FroalaEditorInstance, images: File[] | FileList): boolean {
        // Convert FileList to array if needed
        const imageArray = Array.from(images)

        // First validate the image files
        if (!validateImageUpload(imageArray)) {
          return false
        }

        // If no upload URL is configured, convert to base64 and insert directly
        if (!url) {
          const editor = this as FroalaEditorInstance & {
            image: {
              insert: (src: string, sanitize: boolean, attrs: Record<string, string> | null, $img?: unknown) => void
            }
          }

          // Convert each image to base64 and insert
          imageArray.forEach((file) => {
            const reader = new FileReader()
            reader.onload = (e) => {
              const base64 = e.target?.result as string
              if (base64 && editor.image) {
                editor.image.insert(base64, true, null)
              }
            }
            reader.readAsDataURL(file)
          })

          // Return false to prevent the default upload behavior
          return false
        }

        return true
      },
      // Note: image.beforeInsertByUrl not needed - image-by-URL feature is disabled
      // Only local file upload (converted to base64) is supported via imageInsertButtons: ['imageUpload']
      // Handle image upload errors
      'image.error': function (this: FroalaEditorInstance, error: { code: number; message: string }) {
        showSecurityError(error.message || 'Image upload failed. Please try again.')
      },
      // Validate content when editor is initialized
      initialized: function (this: FroalaEditorInstance) {
        validateAllLinksInEditor(this)
      },
      // Validate when content changes (for programmatic updates)
      contentChanged: function (this: FroalaEditorInstance) {
        // Debounce this in production if performance is a concern
        validateAllLinksInEditor(this)
      },
    },
  }
}
